import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Settings, Globe, Layers } from 'lucide-react';
import Footer from '../components/Footer';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(26,29,36,0.95)), url('https://static.prod-images.emergentagent.com/jobs/9e999b6a-85cd-4b74-86b8-71790862301d/images/5b9049b221280179311d6ed87b87481a7ef9c3a3df746296fb4cb7d1455355ed.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-32 relative z-10">
          <div className="max-w-4xl">
            {/* Overline */}
            <div
              data-testid="hero-overline"
              className="text-xs font-bold uppercase tracking-[0.2em] text-omni-accent mb-6"
            >
              Precision Engineering Excellence
            </div>

            {/* Main Headline */}
            <h1
              data-testid="hero-headline"
              className="text-5xl lg:text-7xl font-heading font-light tracking-tighter text-white mb-6 leading-[1.1]"
            >
              Precision Engineered Metal Solutions for Global Industries
            </h1>

            {/* Subheadline */}
            <p
              data-testid="hero-subheadline"
              className="text-base lg:text-lg text-omni-text-secondary leading-relaxed mb-10 max-w-2xl"
            >
              Premium Stockist & Contract Manufacturing of Aluminium & Stainless Steel Round
              Bars and Pipes for Aerospace and Automotive Applications.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/products"
                data-testid="hero-cta-products"
                className="bg-omni-accent text-white px-8 py-4 hover:bg-omni-accent-hover transition-all font-medium flex items-center justify-center gap-2 group"
              >
                View Product Range
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="bg-transparent border border-omni-accent text-omni-accent px-8 py-4 hover:bg-omni-accent/10 transition-all font-medium flex items-center justify-center gap-2"
              >
                Submit RFQ / Contact
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* At a Glance Section */}
      <section data-testid="at-a-glance-section" className="py-24 lg:py-32 bg-omni-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-omni-accent mb-4">
              Core Capabilities
            </div>
            <h2 className="text-4xl lg:text-5xl font-heading font-normal tracking-tight text-white">
              At a Glance
            </h2>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Card 1: Global Material Supply */}
            <div
              data-testid="capability-card-supply"
              className="bg-omni-background border border-white/10 p-8 hover:border-omni-accent/50 transition-colors shadow-[0_8px_32px_rgba(0,0,0,0.4)] group"
            >
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 bg-omni-accent/10 flex items-center justify-center flex-shrink-0 group-hover:bg-omni-accent/20 transition-colors">
                  <Globe className="w-6 h-6 text-omni-accent" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-heading font-medium tracking-tight text-white mb-4">
                    Global Material Supply
                  </h3>
                  <p className="text-base text-omni-text-secondary leading-relaxed mb-6">
                    Comprehensive stockist of premium-grade Aluminium and Stainless Steel Round
                    Bars, Hollow Bars, and Pipes. Sourced from certified mills worldwide,
                    ensuring compliance with international aerospace and automotive standards.
                  </p>
                  <ul className="space-y-2 text-sm text-omni-text-secondary">
                    <li className="flex items-center gap-2">
                      <div className="w-1 h-1 bg-omni-accent" />
                      Material certification to ASTM, EN, and DIN standards
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1 h-1 bg-omni-accent" />
                      Multiple grades and sizes in stock
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1 h-1 bg-omni-accent" />
                      Fast delivery and custom cut-to-length services
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Card 2: Precision Manufacturing */}
            <div
              data-testid="capability-card-manufacturing"
              className="bg-omni-background border border-white/10 p-8 hover:border-omni-accent/50 transition-colors shadow-[0_8px_32px_rgba(0,0,0,0.4)] group relative overflow-hidden"
            >
              <div
                className="absolute inset-0 opacity-10"
                style={{
                  backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/9e999b6a-85cd-4b74-86b8-71790862301d/images/d9f891249d0e86b9d2d4eb6a2c9f94ea4d7762dcc0d46494199ec2369f2bf13f.png')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
              <div className="relative z-10 flex items-start gap-6">
                <div className="w-12 h-12 bg-omni-accent/10 flex items-center justify-center flex-shrink-0 group-hover:bg-omni-accent/20 transition-colors">
                  <Settings className="w-6 h-6 text-omni-accent" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-heading font-medium tracking-tight text-white mb-4">
                    High-Tolerance Precision Component Manufacturing
                  </h3>
                  <p className="text-base text-omni-text-secondary leading-relaxed mb-6">
                    Advanced CNC machining and contract manufacturing services transforming raw
                    bar and pipe stock into mission-critical components for aerospace defense and
                    automotive Tier-1 applications.
                  </p>
                  <ul className="space-y-2 text-sm text-omni-text-secondary">
                    <li className="flex items-center gap-2">
                      <div className="w-1 h-1 bg-omni-accent" />
                      Ultra-tight tolerances (±0.05mm to ±0.1mm)
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1 h-1 bg-omni-accent" />
                      Multi-axis CNC turning and precision threading
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1 h-1 bg-omni-accent" />
                      Full traceability and quality documentation
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section
        data-testid="industries-section"
        className="py-24 lg:py-32 bg-omni-background-secondary"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-omni-accent mb-4">
              Industries We Serve
            </div>
            <h2 className="text-4xl lg:text-5xl font-heading font-normal tracking-tight text-white">
              Built for Extreme Performance
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Aerospace */}
            <div
              data-testid="industry-card-aerospace"
              className="group relative overflow-hidden h-80"
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{
                  backgroundImage: `url('https://images.unsplash.com/photo-1705614681501-a7f60c15bc97?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxhZXJvc3BhY2UlMjBlbmdpbmVlcmluZyUyMG1hbnVmYWN0dXJpbmd8ZW58MHx8fHwxNzc5MzAwNTM0fDA&ixlib=rb-4.1.0&q=85')`,
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="w-12 h-12 bg-omni-accent/20 backdrop-blur-sm flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-omni-accent" />
                </div>
                <h3 className="text-2xl font-heading font-medium text-white mb-2">
                  Aerospace Engineering
                </h3>
                <p className="text-omni-text-secondary text-sm leading-relaxed">
                  High-strength aluminum alloys and stainless steel components meeting stringent
                  aerospace defense specifications.
                </p>
              </div>
            </div>

            {/* Automotive */}
            <div
              data-testid="industry-card-automotive"
              className="group relative overflow-hidden h-80"
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{
                  backgroundImage: `url('https://images.pexels.com/photos/36598766/pexels-photo-36598766.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="w-12 h-12 bg-omni-accent/20 backdrop-blur-sm flex items-center justify-center mb-4">
                  <Settings className="w-6 h-6 text-omni-accent" />
                </div>
                <h3 className="text-2xl font-heading font-medium text-white mb-2">
                  Automotive Manufacturing
                </h3>
                <p className="text-omni-text-secondary text-sm leading-relaxed">
                  Precision-engineered round bars and custom components for Tier-1 automotive
                  suppliers.
                </p>
              </div>
            </div>

            {/* Die & Mould */}
            <div
              data-testid="industry-card-die-mould"
              className="group relative overflow-hidden h-80"
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{
                  backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/9e999b6a-85cd-4b74-86b8-71790862301d/images/d9f891249d0e86b9d2d4eb6a2c9f94ea4d7762dcc0d46494199ec2369f2bf13f.png')`,
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="w-12 h-12 bg-omni-accent/20 backdrop-blur-sm flex items-center justify-center mb-4">
                  <Layers className="w-6 h-6 text-omni-accent" />
                </div>
                <h3 className="text-2xl font-heading font-medium text-white mb-2">
                  Die & Mould
                </h3>
                <p className="text-omni-text-secondary text-sm leading-relaxed">
                  Premium aluminium plates for tooling, injection moulds, and precision dies with
                  superior machinability.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 bg-omni-background">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl lg:text-5xl font-heading font-normal tracking-tight text-white mb-6">
            Ready to Source Premium Materials?
          </h2>
          <p className="text-base lg:text-lg text-omni-text-secondary leading-relaxed mb-10 max-w-2xl mx-auto">
            Get in touch with our technical team to discuss your material requirements or request
            a quote for contract manufacturing services.
          </p>
          <Link
            to="/contact"
            data-testid="cta-contact-button"
            className="inline-flex bg-omni-accent text-white px-8 py-4 hover:bg-omni-accent-hover transition-all font-medium items-center gap-2 group"
          >
            Request a Quote
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
